// src/components/workflows/views/ManualTriggerButton.jsx - Manual trigger button for automation dashboard
import React, { useState } from 'react';
import { Play, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button.tsx';

/**
 * ManualTriggerButton - Button to manually trigger automation
 *
 * Props:
 *   onTrigger: () => Promise<void> - callback to trigger automation
 *   isRunning: boolean - whether automation is currently running
 *   disabled: boolean - whether button is disabled
 */
const ManualTriggerButton = ({ onTrigger, isRunning = false, disabled = false }) => {
  const [isTriggering, setIsTriggering] = useState(false);

  const handleClick = async () => {
    if (isTriggering || isRunning || disabled) return;

    setIsTriggering(true);
    try {
      await onTrigger?.();
    } finally {
      // Small delay to show feedback
      setTimeout(() => setIsTriggering(false), 500);
    }
  };

  const showLoading = isTriggering || isRunning;

  return (
    <Button
      onClick={handleClick}
      disabled={showLoading || disabled}
      variant="ghost"
      className="h-9 px-4 bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 text-foreground shadow-[0_2px_4px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.1)] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.25),0_6px_16px_rgba(255,255,255,0.15),0_4px_8px_rgba(0,0,0,0.5)] hover:border-zinc-500 hover:-translate-y-0.5 hover:bg-gradient-to-br hover:from-zinc-700 hover:to-zinc-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
    >
      {showLoading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          {isRunning ? 'Running...' : 'Starting...'}
        </>
      ) : (
        <>
          <Play className="w-4 h-4 mr-2" />
          Run Now
        </>
      )}
    </Button>
  );
};

export default ManualTriggerButton;
